import { Component, Prop, State, Method, Event, Host, Watch, Element, Listen, h } from '@stencil/core';

/**
 * Badges help draw attention to important content and can provide additional context to UI elements.
 */
@Component({
  tag: 'som-list-parent-item',
  styleUrl: 'som-list-parent-item.scss',
  scoped: true,
})
export class SomListParentItem {
  @Element() el: HTMLElement;
  buttonDropdownEl: HTMLSomButtonDropdownElement;
  accrodionEl: HTMLSomAccordionElement;

  /**
   * This property represents the name that is displayed as the parent in the list.
   */
  @Prop() displayName: string = '';

  /**
   * This property identifies the type of list.
   */
  @Prop({reflect: true}) type: 'header' | 'panel' | 'sidebar' = 'panel';

  /**
   * This property represents the horizontal position of the dropdown menu. This is used for header menu items with children and is helpful when the last item aligns to the right.
   */
  @Prop() horizontalPosition: 'right' | 'left' = 'left';

  /**
   * This Boolean is used to set the divider. 
   */
  @Prop() showDivider: boolean = false;

  /**
   * This property will toggle the open state.
   */
  @Prop({ mutable: true, reflect: true }) collapsed: boolean = true;

  @State() animating = false;

  /**
   * This event is emitted after the list closes.
   */
  @Event() listClosed;
  /**
   * This event is emitted after the list opens.
   */
  @Event() listOpened;
  /**
   * This event is emitted before the list opens.
   */
  @Event() beforeListOpened;
  /**
   * This event is emitted before the list closes.
   */
  @Event() beforeListClosed;

  childListEl;
  parentListEl;
  backButtonEl;

  // Focus & tabbable elements
  focusedElBeforeOpen;
  a;
  focusableEls;
  firstFocusableEl;
  lastFocusableEl;


  componentWillLoad() {
    this.injectAttrs();
  }

  @Watch('type')
  attrsUpdated() {
    this.injectAttrs();
  }

  injectAttrs() {
    this.el.childNodes.forEach(element => {
      let _type;
      switch (this.type) {
        case 'header':
          _type = 'dropdown-nav';
          break;
        case 'sidebar':
          _type = 'sidebar-child';
          break;
        case 'panel':
          _type = 'panel-child';
          break;
        default:
          _type = this.type;    
          break;
      }
      //element["type"] = (this.type === 'header') ? 'dropdown-nav' : this.type ;
      element["type"] = _type;
    });  
  }

  /**
   * Listen for a panel close event and close the child menu
   */
  @Listen('beforeClose', { target: 'window' })
  handlePanelClose() {
    this.close();
  }

  /**
   * This method will open the panel.
   * @returns Promise will resolve when the open animation ends.
   */
  @Method()
  async open() {
    if (this.collapsed) {
      this.beforeListOpened.emit();
      this.listOpened.emit();

      this.collapsed = false;
      (this.type === 'header') ? this.buttonDropdownEl.open() : this.accrodionEl.show();
    }
  }

  /**
   * This method will close the panel.
   * @returns Promise will resolve when the close animation ends.
   */
  @Method()
  async close() {
    if (!this.collapsed) {
      this.beforeListClosed.emit();
      this.listClosed.emit();

      this.collapsed = true;
      (this.type === 'header') ? this.buttonDropdownEl.close() : this.accrodionEl.hide();
    }
  }

  determineRender() {
    switch (this.type) {
      case 'header':
        return this.renderHeader();
      case 'panel':
        return this.renderPanel();
      case 'sidebar': 
        return this.renderSidebar();
      default:
        return this.renderPanel()
    }
  }

  renderHeader() {
    return (
      <som-button-dropdown class={`list-parent-item ${this.showDivider && 'border-left'} ${this.type}`} button-text={this.displayName} button-type="clear" color="nav" horizontal-position={this.horizontalPosition} collapsed={this.collapsed} ref={ref => { this.buttonDropdownEl = ref; }}>
        <slot></slot>
      </som-button-dropdown>
    );
  }

  renderSidebar() {
    return (
      <som-accordion label={this.displayName} collapsed={this.collapsed} ref={ref => { this.accrodionEl = ref; }}>
        <slot></slot>
      </som-accordion>
    );
  }

  renderPanel () {
    return (
      <som-accordion label={this.displayName} collapsed={this.collapsed} ref={ref => { this.accrodionEl = ref; }}>
        <div class="panel-child-container">
          <slot></slot>
        </div>
      </som-accordion>
    );
  }

  render() {
    return (
      <Host class={`${!this.collapsed ? 'open' : 'closed'}`}>
        {this.determineRender()}
      </Host>
    );
  }
}
